<template>
  <div
    v-if="!empty(myTeam.currentOrder)"
    class="mx-2 my-1"
  >
    <b-button
      variant="outline-primary"
      class="text-nowrap"
      pill
      block
      @click="goToCurrentOrder"
    >
      <feather-icon
        icon="FileTextIcon"
        class="mr-50"
      />
      {{ $t('team.current-order-button') }}
    </b-button>
  </div>
</template>
<script>
import { BButton } from 'bootstrap-vue'
import PusherClient from '@/libs/pusher'
import { empty, parseRequestError } from '@/helpers/helpers'
import { TEAM_ACTIVE_ORDER_STATUSES } from '@/constants/team'

const FINAL_ORDER_STATUSES = ['completed', 'done']

export default {
  components: {
    BButton,
  },
  data() {
    return {
      boundTeam: null,
      teamUpdateHandler: null,
      isTeamRefreshInProgress: false,
      shouldRefetchTeamAfterUpdate: false,
      lastUnassignedOrderId: null,
      isComponentDestroyed: false,
      latestEditedOrderStatus: {
        id: null,
        status: null,
      },
    }
  },
  computed: {
    myTeam() {
      return this.$store.getters['auth/getUserTeam']
    },
    currentOrderId() {
      const id = this.myTeam?.currentOrder?.id
      const parsed = parseInt(id, 10)
      return Number.isNaN(parsed) ? null : parsed
    },
    currentOrderStatus() {
      return this.myTeam?.currentOrder?.status || null
    },
    editedOrder() {
      return this.$store.getters['order/getOrderData']
    },
  },
  watch: {
    myTeam: {
      handler(newTeam) {
        if (newTeam?.currentOrder?.id) {
          this.lastUnassignedOrderId = null
        }
        if (this.boundTeam === null && newTeam && newTeam.id) {
          this.bindTeamUpdates()
          this.boundTeam = newTeam.id
        }
      },
      deep: true,
    },
    editedOrder: {
      handler(order = {}) {
        this.latestEditedOrderStatus = {
          id: order?.id ? parseInt(order.id, 10) : null,
          status: order?.status || null,
        }
      },
      deep: true,
      immediate: true,
    },
  },
  created() {
    this.teamUpdateHandler = this.handleTeamUpdate.bind(this)
  },
  beforeDestroy() {
    this.isComponentDestroyed = true
    PusherClient.unbind('update', 'team', this.teamUpdateHandler, this)
  },
  methods: {
    bindTeamUpdates() {
      PusherClient.bind('update', 'team', this.teamUpdateHandler, this)
    },
    handleTeamUpdate(data) {
      if (this.isComponentDestroyed) {
        return
      }
      if (!this.myTeam || !this.myTeam.id) {
        return
      }
      if (parseInt(data.id, 10) !== parseInt(this.myTeam.id, 10)) {
        return
      }
      this.processTeamUpdateFromPusher()
    },
    processTeamUpdateFromPusher() {
      if (this.isComponentDestroyed) {
        return
      }
      if (this.isTeamRefreshInProgress) {
        this.shouldRefetchTeamAfterUpdate = true
        return
      }
      this.fetchLatestTeamData()
    },
    fetchLatestTeamData() {
      const previousOrderId = this.myTeam?.currentOrder?.id
      const previousOrderStatus = this.myTeam?.currentOrder?.status
      this.isTeamRefreshInProgress = true
      this.$store.dispatch('auth/fetchUserTeam')
        .then(response => {
          const updatedTeam = response && response.data ? response.data : this.myTeam
          const isOrderUnassigned = previousOrderId && (!updatedTeam.currentOrder || !updatedTeam.currentOrder.id)
          const shouldNotify = isOrderUnassigned && this.isOrderStatusActive(previousOrderStatus)
          if (shouldNotify) {
            this.handleCurrentOrderUnassigned(previousOrderId)
          }
        })
        .catch(err => {
          parseRequestError(err)
        })
        .finally(() => {
          this.isTeamRefreshInProgress = false
          if (this.shouldRefetchTeamAfterUpdate) {
            this.shouldRefetchTeamAfterUpdate = false
            this.fetchLatestTeamData()
          }
        })
    },
    isOrderStatusActive(status) {
      if (!status) {
        return false
      }
      return TEAM_ACTIVE_ORDER_STATUSES.includes(status)
    },
    handleCurrentOrderUnassigned(orderId) {
      if (this.isComponentDestroyed) {
        return
      }
      if (parseInt(this.lastUnassignedOrderId, 10) === parseInt(orderId, 10)) {
        return
      }
      if (this.isOrderFinalizedLocally(orderId)) {
        return
      }
      this.lastUnassignedOrderId = orderId
      window.toast.notify.warning(
        this.$t('order.notifications.team-unassigned.title'),
        this.$t('order.notifications.team-unassigned.text'),
      )
      this.$store.commit('order/CLEAR_ORDER_EDITED')
      if (
        this.$router.currentRoute.name === 'order-view'
        && parseInt(this.$router.currentRoute.params.id, 10) === parseInt(orderId, 10)
      ) {
        this.$router.push('/order/list')
      }
    },
    isOrderFinalizedLocally(orderId) {
      const numericId = parseInt(orderId, 10)
      if (!numericId) {
        return false
      }
      const edited = this.latestEditedOrderStatus
      if (!edited || !edited.id || edited.id !== numericId) {
        return false
      }
      return FINAL_ORDER_STATUSES.includes(edited.status)
    },
    addNewOrder() {
      this.$router.push('/order/create')
    },
    goToCurrentOrder() {
      if (this.$router.currentRoute.path === this.getPath()) {
        return
      }

      this.$router.push(this.getPath())
    },
    getPath() {
      return `/order/view/${this.myTeam.currentOrder.id}`
    },
    empty,
  },
}
</script>
